import {
  Component,
  OnInit,
  OnDestroy,
  HostListener,
  ElementRef,
  ViewChild,
  Output,
  EventEmitter,
} from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Router, NavigationStart } from '@angular/router';
import { SearchResult, SearchService } from 'src/app/services/search.service';
import { selectSearchId } from 'src/app/store/selectors/files.selector';
import { searchClick } from 'src/app/store/actions/files.actions';
import { debounce, distinctUntilChanged, filter, map, switchMap, take } from 'rxjs/operators';
import { take as takeOperator } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { clearSearch, search } from 'src/app/store/actions/files.actions';
import { combineLatest, timer, Observable, Subscription } from 'rxjs';
import { selectSearchIsLoading, selectSearchQuery, selectSearchResults } from 'src/app/store/selectors/files.selector';
import { selectDomain, selectRoles } from 'src/app/store/selectors/auth.selectors';
import { environment } from 'src/environments/environment';
import { ArticleSearchService, ArticleSearchResult } from 'src/app/services/article-search.service';
import { searchResultAnimations } from './search-files.animations';
import { Router as AngularRouter } from '@angular/router';
import { SearchStateService } from 'src/app/services/search-state.service';
import { ReadTrackingService } from 'src/app/services/read-tracking.service';
import { AiOverlayService } from 'src/app/services/ai-overlay.service';
import { Domain } from 'src/app/services/domain.service';
import { canUseAiAssistant } from 'src/app/utils/ai-access';

const MIN_SEARCH_QUERY_LENGTH = 3;
const SHORT_SEARCH_QUERY_EXCEPTIONS = new Set(['pv']);

const normalizeSearchQuery = (query: string | null | undefined): string => {
  return (query || '').trim().toLowerCase();
};

const isSearchAllowed = (query: string | null | undefined): boolean => {
  const value = normalizeSearchQuery(query);

  return value.length >= MIN_SEARCH_QUERY_LENGTH || SHORT_SEARCH_QUERY_EXCEPTIONS.has(value);
};

const searchLengthValidator = (control: AbstractControl): ValidationErrors | null => {
  const value = normalizeSearchQuery(control.value);

  if (!value || isSearchAllowed(value)) {
    return null;
  }

  return {
    minlength: {
      requiredLength: MIN_SEARCH_QUERY_LENGTH,
      actualLength: value.length,
    },
  };
};

@Component({
  selector: 'mvp-search-files',
  templateUrl: './search-files.component.html',
  animations: searchResultAnimations,
})
export class SearchFilesComponent implements OnInit, OnDestroy {
  showSuggestionBox: boolean = false;
  searchId$ = this.store.select(selectSearchId);
  @ViewChild('searchInput') searchInput: ElementRef;
  @ViewChild('articlesList', { static: false }) articlesList: ElementRef<HTMLUListElement>;

  form: FormGroup = this.formBuilder.group({ input: ['', [Validators.required, searchLengthValidator]] });
  isLoading$: Observable<boolean>;
  searchResults$: Observable<SearchResult[]>;
  searchValue$: Observable<string>;
  searchPlaceholder$: Observable<string>;

  notionSuggestions: string[] = [];
  currentQuery = '';
  currentResults: SearchResult[] = [];
  articleResults: ArticleSearchResult[] = [];
  combinedResults: SearchResult[] = [];
  
  activeArticleIndex: number = 0;
  assetUrl: string = environment.assetUrl;
  environment = environment;

  private lastActive = false;
  private articleSearchRequestId = 0;
  private articleSearchSubscription?: Subscription;
  private aiOverlaySubscription?: Subscription;
  private clearSearchWhenOverlayCloses = false;
  private revealTimeouts: Array<ReturnType<typeof setTimeout>> = [];
  searchLaunched = false;
  showArticles = false;
  showRemainingFaqs = false;

  @Output() searchActiveChange = new EventEmitter<boolean>();

  constructor(
    private router: Router,
    private formBuilder: FormBuilder,
    private store: Store,
    private searchService: SearchService,
    private elementRef: ElementRef,
    private articleSearchService: ArticleSearchService,
    private angularRouter: AngularRouter,
    private searchStateService: SearchStateService,
    private readTrackingService: ReadTrackingService,
    private aiOverlayService: AiOverlayService
  ) {
    this.searchResults$ = this.store.select(selectSearchResults);
    this.isLoading$ = this.store.select(selectSearchIsLoading);
    this.searchValue$ = this.store.select(selectSearchQuery);
    this.searchPlaceholder$ = combineLatest([
      this.store.select(selectDomain),
      this.store.select(selectRoles),
    ]).pipe(
      map(([domain, roles]: [Domain, string[]]) => canUseAiAssistant(domain, roles)
        ? 'Cherchez une réponse juridique'
        : 'Cherchez une réponse'
      )
    );
  }

  ngOnInit() {
    this.aiOverlaySubscription = this.aiOverlayService.overlay$.subscribe(({ isOpen, firstMessage }) => {
      if (isOpen) {
        this.clearSearchWhenOverlayCloses = !!firstMessage?.trim();
        return;
      }

      if (this.clearSearchWhenOverlayCloses) {
        this.clearSearchWhenOverlayCloses = false;
        this.resetSearchInput();
      }
    });

    this.form.get('input').valueChanges.subscribe(value => {
      this.showSuggestionBox = (value?.length || 0) > 0;
      this.searchStateService.setSearchValue(value);
    });
    const specials = ["aide", "jour", "nuit", "paie"];
    this.form.get('input').valueChanges.pipe(
      distinctUntilChanged(),
      filter((value: string) => value.length === 0 && (this.currentResults?.length === 0 || this.searchLaunched) && this.currentQuery?.length > 0)
    ).subscribe(() => this.resetSearchInput());
    this.form.get('input').valueChanges.pipe(
      filter((value: string) => value.length >= 5 || specials.includes(value.toLowerCase())),
      debounce(() => timer(500)),
      // distinctUntilChanged(),
      switchMap((query) => this.searchNotions(query))
    ).subscribe((result: any) => {
      if (Array.isArray(result)) {
        this.notionSuggestions = result;
      }
    });

    this.searchValue$.subscribe(val => this.currentQuery = val || '');
    this.searchResults$.subscribe(res => {
      this.currentResults = Array.isArray(res) ? res : [];
      if (!this.waitingForArticleResults) {
        this.updateCombinedResults();
      }
    });

    this.searchValue$.pipe(take(1)).subscribe(val => {
      if (val) {
        this.form.get('input').setValue(val, { emitEvent: false });
        this.startArticleSearch(val);
      }
    });
    this.angularRouter.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        if (this.isNewsHomeUrl(event.url)) {
          setTimeout(() => {
            this.searchStateService.setSearchActive(this.searchLaunched || this.combinedResults.length > 0);
          }, 0);
        }
      }
    });
  }

  ngOnDestroy() {
    this.cancelArticleSearch();
    this.aiOverlaySubscription?.unsubscribe();
    this.clearRevealTimeouts();
  }

  onInputFocus() {
    this.showSuggestionBox = true;
  }

  closeSuggestions() {
    this.showSuggestionBox = false;
  }

  searchNotions(query: string) {
    return this.searchService.getNotionSuggestions(query);
  }
  
  selectSuggestion(event: MouseEvent, suggestion: string) {
    event.stopPropagation();
    this.showSuggestionBox = false;
    this.notionSuggestions = [];
    this.showArticles = false;
    this.showRemainingFaqs = false;
    this.form.get('input').setValue(suggestion, { emitEvent: false });
    combineLatest([
      this.store.select(selectDomain),
      this.store.select(selectRoles),
    ]).pipe(takeOperator(1)).subscribe(([domain, roles]: [Domain, string[]]) => {
      if (this.canUseChatbotAi(domain, roles)) {
        this.aiOverlayService.open(suggestion);
        return;
      }

      this.store.dispatch(search({ query: suggestion }));
    });
  }

  waitingForArticleResults = false;

  onSubmit() {
    const query = this.form.get('input').value;
    if (isSearchAllowed(query)) {
      combineLatest([
        this.store.select(selectDomain),
        this.store.select(selectRoles),
      ]).pipe(takeOperator(1)).subscribe(([domain, roles]: [Domain, string[]]) => {
        if (this.canUseChatbotAi(domain, roles)) {
          this.showSuggestionBox = false;
          this.aiOverlayService.open(query);
          return;
        }

        this.launchNormalSearch(query);
      });
    }
  }

  private canUseChatbotAi(domain: Domain, roles: string[] = []): boolean {
    return canUseAiAssistant(domain, roles);
  }

  private launchNormalSearch(query: string) {
    this.clearRevealTimeouts();
    this.showSuggestionBox = true;
    this.searchLaunched = true;
    this.showArticles = false;
    this.showRemainingFaqs = false;
    this.waitingForArticleResults = true;
    this.searchActiveChange.emit(true);
    this.searchStateService.setSearchLaunched(true);
    this.store.dispatch(search({ query }));
    this.startArticleSearch(query, () => {
      this.lastActive = true;
    });
  }

  resetSearchInput() {
    this.cancelArticleSearch();
    this.clearRevealTimeouts();
    this.currentResults = [];
    this.articleResults = [];
    this.combinedResults = [];
    this.waitingForArticleResults = false;
    this.store.dispatch(clearSearch());
    this.notionSuggestions = [];
    this.showSuggestionBox = false;
    this.form.get('input').setValue('', { emitEvent: false });
    this.searchLaunched = false;
    this.showArticles = false;
    this.showRemainingFaqs = false;
    this.searchActiveChange.emit(false);
    this.searchStateService.clear();
    this.lastActive = false;
  }

  private normalizeType(type: string | undefined | null): string {
    if (!type) {
      return '';
    }
    return type
      .toLowerCase()
      .replace(/[’']/g, "'")
      .trim();
  }

  private sortResultsByTypePriority(results: SearchResult[]): SearchResult[] {
    const level1Types = new Set([
      'accord entreprise',
      "accord d'entreprise",
      "accord d’entreprise",
      'décision unilatérale',
      'charte',
      'code général de la fonction publique',
    ]);
    const level2Types = new Set(['convention collective']);
    const level3Types = new Set(['code du travail']);

    const level1: SearchResult[] = [];
    const level2: SearchResult[] = [];
    const level3: SearchResult[] = [];
    const others: SearchResult[] = [];

    results.forEach(result => {
      const normalizedType = this.normalizeType(result.type);
      if (level1Types.has(normalizedType)) {
        level1.push(result);
      } else if (level2Types.has(normalizedType)) {
        level2.push(result);
      } else if (level3Types.has(normalizedType)) {
        level3.push(result);
      } else {
        others.push(result);
      }
    });

    if (level1.length > 0 || level2.length > 0) {
      return [...level1, ...level2, ...others];
    }

    return [...level3, ...others];
  }

  private startArticleSearch(query: string, onComplete?: () => void): void {
    const requestId = ++this.articleSearchRequestId;
    this.articleSearchSubscription?.unsubscribe();
    this.articleSearchSubscription = this.articleSearchService.searchArticles(query).subscribe({
      next: (res) => {
        if (!this.isCurrentArticleSearch(requestId)) {
          return;
        }

        this.articleResults = res.results || [];
        this.waitingForArticleResults = false;
        this.updateCombinedResults();
        onComplete?.();
      },
      error: () => {
        if (!this.isCurrentArticleSearch(requestId)) {
          return;
        }

        this.articleResults = [];
        this.waitingForArticleResults = false;
        this.updateCombinedResults();
        onComplete?.();
      },
    });
  }

  private cancelArticleSearch(): void {
    this.articleSearchRequestId++;
    this.articleSearchSubscription?.unsubscribe();
    this.articleSearchSubscription = undefined;
  }

  private isCurrentArticleSearch(requestId: number): boolean {
    return requestId === this.articleSearchRequestId;
  }

  private clearRevealTimeouts(): void {
    this.revealTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
    this.revealTimeouts = [];
  }

  private scheduleReveal(callback: () => void, delay: number): void {
    const timeoutId = setTimeout(() => {
      this.revealTimeouts = this.revealTimeouts.filter(id => id !== timeoutId);
      callback();
    }, delay);

    this.revealTimeouts.push(timeoutId);
  }

  private updateCombinedResults() {
    if (this.waitingForArticleResults) return;
    this.clearRevealTimeouts();
    const sortedFaqAndNewsResults = this.sortResultsByTypePriority(this.currentResults || []);
    const mappedArticleResults = this.articleResults.map(a => ({
      uuid: a.uuid,
      title: a.title,
      response: a.body,
      keywords: [],
      is_enabled: a.is_enabled,
      thumbnail: a.thumbnail,
      from: 'article' as 'faq' | 'news' | 'article',
      type: 'Article',
      category: '',
      created_at: a.created_at ? (typeof a.created_at === 'number' ? new Date(a.created_at * 1000).toISOString() : a.created_at) : undefined,
      publication_date: a.publication_date ? (typeof a.publication_date === 'number' ? new Date(a.publication_date * 1000).toISOString() : a.publication_date) : undefined
    }));

    this.combinedResults = [
      ...sortedFaqAndNewsResults,
      ...mappedArticleResults
    ];
    this.searchStateService.setSearchResults(this.combinedResults);
    this.updateActiveArticleChip();
    
    const firstFaqCount = Math.min(this.getNonArticleResults().length, 6);
    const hasArticles = this.getArticleResults().length > 0;
    const hasRemainingFaqs = this.getRemainingFaqResults().length > 0;
    
    const articleDelay = firstFaqCount > 0 ? (firstFaqCount * 80) + 250 + 200 : 400;
    const remainingFaqDelay = hasArticles ? articleDelay + 600 : articleDelay;
    
    if (hasArticles) {
      this.scheduleReveal(() => {
        this.showArticles = this.combinedResults.length > 0 && this.getArticleResults().length > 0;
      }, articleDelay);
    } else {
      this.showArticles = false;
    }
    
    if (hasRemainingFaqs) {
      this.scheduleReveal(() => {
        this.showRemainingFaqs = this.combinedResults.length > 0 && this.getRemainingFaqResults().length > 0;
      }, remainingFaqDelay);
    } else {
      this.showRemainingFaqs = false;
    }
  }

  async goToResult(result: SearchResult) {
    this.searchId$.pipe(takeOperator(1)).subscribe(id => {
      if (id != null) {
        this.store.dispatch(searchClick({ searchId: id }));
      }

      const searchQueryParams = this.readTrackingService.withSearchSource(
        result.from === 'faq'
          ? { fromSearch: 'true' }
          : {},
        id ?? undefined
      );

      const targetRoute = result.from === 'news' || result.from === 'article'
        ? `article/${result.uuid}`
        : `question/${result.uuid}`;

      this.router.navigate([targetRoute], {
        queryParams: searchQueryParams,
      });
    });
  }

  goToArticle(article: ArticleSearchResult) {
    this.searchId$.pipe(takeOperator(1)).subscribe(id => {
      this.router.navigate([`article/${article.uuid}`], {
        queryParams: this.readTrackingService.withSearchSource({}, id ?? undefined),
      });
    });
  }

  submitSearchContact() {
    this.router.navigate(['/new-chat']);
  }

  @HostListener('document:touchstart', ['$event'])
  onTouchStart(event: TouchEvent) {
    const inputElement = this.searchInput.nativeElement;
    if (
      event.target !== inputElement &&
      inputElement === document.activeElement
    ) {
      inputElement.blur();
    }
  }

  @HostListener('window:scroll', ['$event'])
  onWindowScroll(event: Event) {
    this.closeSuggestions();
  }

  @HostListener('window:wheel', ['$event'])
  onWindowWheel(event: WheelEvent) {
    this.closeSuggestions();
  }

  @HostListener('document:touchmove', ['$event'])
  onDocumentTouchMove(event: TouchEvent) {
    this.closeSuggestions();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
  }

  getThumbnailUrl(article: SearchResult) {
    return `${this.assetUrl}/thumbnails/${article.thumbnail}`;
  }

  getArticleResults(): SearchResult[] {
    return this.combinedResults.filter(result => result.from === 'article');
  }

  getNonArticleResults(): SearchResult[] {
    return this.combinedResults.filter(result => result.from !== 'article');
  }

  getFirstFaqResults(): SearchResult[] {
    const nonArticleResults = this.getNonArticleResults();
    return nonArticleResults.slice(0, 6);
  }

  getRemainingFaqResults(): SearchResult[] {
    const nonArticleResults = this.getNonArticleResults();
    return nonArticleResults.slice(6);
  }

  scrollArticlesLeft() {
    if (this.articlesList?.nativeElement) {
      this.articlesList.nativeElement.scrollBy({ left: -300, behavior: 'smooth' });
    }
  }

  scrollArticlesRight() {
    if (this.articlesList?.nativeElement) {
      this.articlesList.nativeElement.scrollBy({ left: 300, behavior: 'smooth' });
    }
  }

  onArticlesScroll() {
    this.updateActiveArticleChip();
  }

  updateActiveArticleChip() {
    if (!this.articlesList?.nativeElement) return;
    
    const listElement = this.articlesList.nativeElement;
    const scrollLeft = listElement.scrollLeft;
    const viewportWidth = listElement.offsetWidth;

    let maxVisibleWidth = 0;
    let newActiveIndex = 0;

    const children = listElement.children;

    for (let i = 0; i < children.length; i++) {
      const card = children[i] as HTMLElement;
      const cardStart = card.offsetLeft - scrollLeft;
      const cardEnd = cardStart + card.offsetWidth;

      const visibleWidth = Math.min(cardEnd, viewportWidth) - Math.max(cardStart, 0);

      if (visibleWidth > maxVisibleWidth) {
        maxVisibleWidth = visibleWidth;
        newActiveIndex = i;
      }
    }

    this.activeArticleIndex = newActiveIndex;
  }

  activeArticleChipColor() {
    if (this.environment.application === 'tract') {
      return ' bg-[#FFA500] ';
    }
    return ' bg-yellow-400';
  }

  private isNewsHomeUrl(url: string): boolean {
    const path = url.split('?')[0];
    return path === '/news' || path === '/news-bis';
  }
}
